/**
 * 问题状态的「下一步能去哪」。
 *
 * 对应后端 apps/api/src/modules/modules.rules.ts 里的 statusTransitions，
 * 这里只抄问题清单用得到的那几个状态。前端拿它决定下拉里列什么，
 * 真正的校验仍在后端——前端漏了一条，后端会拦下并给出人话提示，
 * 不会写进一个非法状态。
 *
 * legacy_orphan 之类旧系统迁来的状态不在表里：后端给它们开了单向出口
 * （未知状态可以转到任何标准状态），这里照做，否则那 4 条永远动不了。
 */
const TRANSITIONS: Readonly<Record<string, readonly string[]>> = {
  open: ['in_progress', 'closed', 'cancelled'],
  pending: ['in_progress', 'completed', 'rejected', 'cancelled'],
  in_progress: ['completed', 'closed', 'blocked', 'cancelled'],
  blocked: ['in_progress', 'cancelled'],
};

/** 未知状态的出路：只给问题清单里讲得通的那几个，不把文档/变更的状态混进来 */
const FALLBACK: readonly string[] = ['open', 'in_progress', 'blocked', 'completed', 'closed', 'cancelled'];

export const ISSUE_STATUS_LABEL: Readonly<Record<string, string>> = {
  open: '待处理',
  pending: '待处理',
  in_progress: '处理中',
  blocked: '受阻',
  completed: '已完成',
  closed: '已关闭',
  cancelled: '已取消',
  rejected: '已退回',
};

export function issueStatusLabel(status: string): string {
  return ISSUE_STATUS_LABEL[status] ?? status;
}

/** 问题清单里开放就地编辑的列 */
export type InlineField =
  | 'ownerName' | 'severityGrade' | 'category' | 'currentPlanDate' | 'status' | 'progress';

/**
 * 让问题退出待办的状态。
 *
 * 判「问题结束」的人才能推到这几个状态：质量 / 本项目项目经理 / 问题提出人
 * （0928 Tony 从「只有质量能关」放宽；责任人仍然不行）。三个都要管住——
 * 只拦 closed 的话，换「已完成」一样能让问题从待处理里消失。
 */
export const ISSUE_CLOSING_STATUSES: readonly string[] = ['closed', 'completed', 'cancelled'];

/**
 * 从当前状态能走到哪；当前状态本身排在第一位，方便下拉回显。
 *
 * canClose=false 时把关闭类的状态摘掉——给了也点不动（后端会拦），
 * 不如不给，省得人点一次吃个红条才知道自己没这个权限。
 */
export function issueStatusOptions(
  current: string,
  canClose = true,
): Array<{ value: string; label: string }> {
  const next = (TRANSITIONS[current] ?? FALLBACK.filter((item) => item !== current))
    .filter((value) => canClose || !ISSUE_CLOSING_STATUSES.includes(value));
  return [current, ...next].map((value) => ({
    // 旧系统迁来的状态没有中文名，光显示 legacy_orphan 像乱码，标一下出处
    value,
    label: ISSUE_STATUS_LABEL[value] ?? `${value}（旧系统）`,
  }));
}
